import React, { useEffect, useState } from 'react';
import { useAppKit, useAppKitAccount, useAppKitNetwork } from '@reown/appkit/react';
import '../config/appkit'; // Initialize AppKit context once globally
import { useAccount, useChainId, useSwitchChain } from 'wagmi';
// Solana wallet support removed - EVM chains only
import { getEnabledNetworks, getNetworkConfig } from '../config/networks';
import { FEATURES } from '../config/features';
import { isNetworkEnabled, clearWalletConnectSessions, initializeAppKit } from '../config/appkit';

// Safe hook wrappers to handle AppKit initialization failures
function useSafeAppKit() {
  try {
    return useAppKit();
  } catch (error) {
    console.warn('AppKit not initialized, wallet connection disabled:', error);
    return { open: () => console.warn('Wallet connection unavailable') };
  }
}

function useSafeAppKitAccount() {
  try {
    return useAppKitAccount();
  } catch (error) {
    console.warn('AppKit account hook failed:', error);
    return { address: undefined, isConnected: false };
  }
}

function useSafeAppKitNetwork() {
  try {
    return useAppKitNetwork();
  } catch (error) {
    console.warn('AppKit network hook failed:', error);
    return { chainId: undefined };
  }
}

interface WalletConnectProps {
  onNetworkWarning?: (warning: string | null) => void;
  onWalletConnected?: (address: string, network: string) => void;
}

const WalletConnect: React.FC<WalletConnectProps> = ({ 
  onNetworkWarning, 
  onWalletConnected 
}) => {
  const { open } = useSafeAppKit();
  const { address: appKitAddress, isConnected: appKitConnected } = useSafeAppKitAccount();
  const { chainId: appKitChainId } = useSafeAppKitNetwork();
  
  // EVM hooks
  const { address: evmAddress, isConnected: evmConnected } = useAccount();
  const evmChainId = useChainId();
  const { switchChain } = useSwitchChain();
  
  // Determine current connection state - EVM only
  const isConnected = evmConnected || appKitConnected;
  const currentAddress = evmAddress || appKitAddress;
  const currentChainId = evmChainId || appKitChainId;
  
  // Get enabled networks
  const enabledNetworks = getEnabledNetworks();
  const [selectedNetwork, setSelectedNetwork] = useState<string>('Ethereum');
  
  // Check if current network is supported
  const isNetworkSupported = () => {
    if (!currentChainId) return true;

    if (typeof currentChainId === 'number') {
      // EVM network - check against enabled networks
      return enabledNetworks.some(net =>
        'chainId' in net && net.chainId === currentChainId
      );
    }

    // For non-EVM networks - only handle mainnet cases
    const networkName = typeof currentChainId === 'string' ?
      (currentChainId === 'mainnet-beta' ? 'Solana' :
       currentChainId === 'mainnet' ? 'Bitcoin' : currentChainId) : '';

    return networkName ? isNetworkEnabled(networkName) : false;
  };

  // Network warning effect
  useEffect(() => {
    if (isConnected && !isNetworkSupported()) {
      onNetworkWarning?.('Please switch to a supported network');
    } else {
      onNetworkWarning?.(null);
    }
  }, [isConnected, currentChainId, onNetworkWarning]);

  // Wallet connected callback
  useEffect(() => {
    if (isConnected && currentAddress) {
      let network = 'Unknown';
      
      if (evmConnected && evmChainId) {
        const networkConfig = enabledNetworks.find(net => 
          'chainId' in net && net.chainId === evmChainId
        );
        network = networkConfig?.name || 'Unknown EVM';
      } else if (solanaConnected) {
        network = 'Solana';
      }
      
      onWalletConnected?.(currentAddress, network);
    }
  }, [isConnected, currentAddress, evmChainId, evmConnected, solanaConnected, onWalletConnected]);

  // Handle connect click
  const handleConnect = () => {
    open();
  };

  // Handle clearing WalletConnect sessions
  const handleClearSessions = () => {
    try {
      clearWalletConnectSessions();
      // Force reinitialize AppKit
      initializeAppKit(true);
      onNetworkWarning?.('WalletConnect sessions cleared. Please try connecting again.');
    } catch (error) {
      console.error('Failed to clear sessions:', error);
      onNetworkWarning?.('Failed to clear sessions. Please refresh the page and try again.');
    }
  };

  // Handle network switch
  const handleNetworkSwitch = async (networkName: string) => {
    const network = getNetworkConfig(networkName);
    if (!network) {
      onNetworkWarning?.(`Network ${networkName} not found`);
      return;
    }
    
    try {
      if ('chainId' in network && switchChain) {
        // EVM network switch
        await switchChain({ chainId: network.chainId });
        setSelectedNetwork(networkName);
        onNetworkWarning?.(null);
      } else {
        // For non-EVM networks, just update selection
        setSelectedNetwork(networkName);
        // AppKit will handle the appropriate wallet connection
        onNetworkWarning?.(null);
      }
    } catch (error) {
      console.error('Failed to switch network:', error);
      
      // Handle specific error types
      if (error instanceof Error) {
        if (error.message.includes('User rejected')) {
          onNetworkWarning?.('Network switch cancelled by user');
        } else if (error.message.includes('Chain not configured')) {
          onNetworkWarning?.(`${networkName} network not configured in your wallet. Please add it manually.`);
        } else {
          onNetworkWarning?.(`Failed to switch to ${networkName}: ${error.message}`);
        }
      } else {
        onNetworkWarning?.(`Failed to switch to ${networkName}. Please try manually in your wallet.`);
      }
    }
  };

  // Render network selector
  const renderNetworkSelector = () => {
    if (!FEATURES.networkSelector || !isConnected) return null;
    
    return (
      <div className="network-selector-dropdown">
        <select 
          value={selectedNetwork} 
          onChange={(e) => handleNetworkSwitch(e.target.value)}
          className="network-select"
        >
          {enabledNetworks.map((network) => (
            <option key={network.network} value={network.network}>
              {network.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // Render connect button
  if (!isConnected) {
    return (
      <div className="wallet-connect">
        <button 
          onClick={handleConnect}
          className="connect-wallet-btn"
          type="button"
        >
          Connect Wallet
        </button>
        <button 
          onClick={handleClearSessions}
          className="clear-sessions-btn"
          type="button"
          title="Clear WalletConnect sessions if you're having connection issues"
          style={{ 
            marginLeft: '8px', 
            fontSize: '12px', 
            padding: '4px 8px',
            background: 'transparent',
            border: '1px solid #666',
            color: '#666',
            cursor: 'pointer'
          }}
        >
          Clear Sessions
        </button>
      </div>
    );
  }

  // Render connected state
  return (
    <div className="wallet-connect">
      <div className="wallet-info">
        {renderNetworkSelector()}
        
        <button
          onClick={() => open()}
          className="account-btn"
          type="button"
        >
          {currentAddress ? 
            `${currentAddress.slice(0, 6)}...${currentAddress.slice(-4)}` : 
            'Connected'
          }
        </button>
      </div>
      
      {!isNetworkSupported() && (
        <div className="network-warning">
          <p>⚠️ Unsupported network</p>
          <div className="network-switch-options">
            {enabledNetworks.map((network) => (
              <button
                key={network.network}
                onClick={() => handleNetworkSwitch(network.network)}
                className="network-option-btn"
              >
                {network.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletConnect;
